/**
 * Tags Cell Utilities
 *
 * Provides data transformation logic for the TagsCell component,
 * specifically handling tag prioritization based on active filters.
 *
 * @module tagsCell.util
 * @see TagsCell.tsx - Component that uses this utility
 */

/**
 * Prioritizes tags based on active table filters
 *
 * Priority Rules:
 * 1. No filters: Alphabetical order
 * 2. With filters: Filtered tags first (alphabetical), then others (alphabetical)
 *
 * @param tags - Tags to prioritize
 * @param activeFilters - Currently selected tag filters from table state
 * @returns Prioritized and sorted array of tags
 *
 * @example
 * ```typescript
 * // No filters - alphabetical
 * getPrioritizedTags(['PRODUCTION', 'TEST', 'DEV'])
 * // => ['DEV', 'PRODUCTION', 'TEST']
 *
 * // With filters - filtered tags first
 * getPrioritizedTags(['PRODUCTION', 'TEST', 'DEV'], ['test', 'dev'])
 * // => ['DEV', 'TEST', 'PRODUCTION']
 * ```
 */
export const getPrioritizedTags = (
  tags: readonly string[],
  activeFilters?: string[]
): string[] => {
  if (!tags.length) return [];

  // No filters active: Alphabetical order
  if (!activeFilters || activeFilters.length === 0) {
    return [...tags].sort();
  }

  // Filters active: Normalize filters and split into tiers
  const normalizedFilters = activeFilters.map(filter => filter.toLowerCase());
  const filteredSet = new Set(normalizedFilters);

  const matchedTags = tags
    .filter(tag => filteredSet.has(tag.toLowerCase()))
    .sort();

  const unmatchedTags = tags
    .filter(tag => !filteredSet.has(tag.toLowerCase()))
    .sort();

  return [...matchedTags, ...unmatchedTags];
};
